import { getApiBaseUrl } from '@/config/app';

export type ClaimResult = 'claimed' | 'alreadyClaimed' | 'walletNotLinked' | 'ineligible' | 'error';

export interface RewardPayout {
  currency: string | null;
  amount: string | number;
  payoutStatus: 'completed' | 'failed' | string;
  payoutTxHash?: string | null;
}

export interface ClaimOutcome {
  result: ClaimResult;
  failedRequirements?: string[];
  rewards?: RewardPayout[];
}

interface ActiveAirdrop {
  code: string;
  [key: string]: unknown;
}

class AirdropService {
  private readonly base = `${getApiBaseUrl()}/api/airdrops`;

  /** Devuelve el code del primer airdrop activo aplicable a OMD_WALLET, o null si no hay ninguno */
  async getActiveAirdropCode(): Promise<string | null> {
    try {
      const res = await fetch(`${this.base}/active`);
      if (!res.ok) return null;

      const body = await res.json();
      const airdrops = (body?.data ?? []) as ActiveAirdrop[];
      return airdrops[0]?.code ?? null;
    } catch {
      return null;
    }
  }

  async claimAirdrop(code: string, walletAddress: string): Promise<ClaimOutcome> {
    try {
      const res = await fetch(`${this.base}/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, walletAddress }),
      });

      const body = await res.json().catch(() => ({}));

      if (res.status === 200) {
        return {
          result: body?.data?.alreadyClaimed ? 'alreadyClaimed' : 'claimed',
          rewards: body?.data?.rewards,
        };
      }

      if (res.status === 404 && body?.code === 'WALLET_NOT_LINKED') {
        return { result: 'walletNotLinked' };
      }

      if (res.status === 403) {
        return { result: 'ineligible', failedRequirements: body?.data?.failedRequirements };
      }

      return { result: 'error' };
    } catch {
      return { result: 'error' };
    }
  }

  /** Code y nombre del airdrop con pago failed definitivo, o null (nunca throw). */
  async getFailedPayoutAirdrop(walletAddress: string): Promise<{ code: string; name: string } | null> {
    try {
      const res = await fetch(`${this.base}/payout-status?walletAddress=${encodeURIComponent(walletAddress)}`);
      if (!res.ok) return null;
      const body = await res.json();
      return body?.data?.airdropCode ? { code: body.data.airdropCode, name: body.data.airdropName } : null;
    } catch {
      return null;
    }
  }
}

export const airdropService = new AirdropService();
